"use client";

import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { FaArrowRight } from "react-icons/fa";
import type { CreateQuickTradePayload } from "@sdk/shared/types/order.types";
import { Button } from "@shared/src/components/ui/button";
import { NetworkChip } from "../../ui/network-chip";
import { TokenChip } from "../../ui/token-chip";

interface QuickTradeSearchingProps {
  formData: CreateQuickTradePayload;
  onCancel: () => Promise<void>;
}

export function QuickTradeSearching({ formData, onCancel }: QuickTradeSearchingProps) {
  const fiatAmount = formData.priceLimit * formData.quantity;

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-lg mx-auto ">
      <div className="text-center space-y-3">
        <h5 className="font-medium text-text-primary">Hang on as we find a merchant...</h5>
        <p>Connecting you to available merchants...</p>
      </div>

      {/* Lottie Animation */}
      <div className="size-44">
        <DotLottieReact
          src="https://lottie.host/8d714ad7-2087-497e-b279-6ea6104fa7ae/2Hp8ikuA1i.lottie"
          loop
          autoplay
        />
      </div>

      {/* Trade Details Card */}
      <div className="border-2 border-border-secondary rounded-3xl p-4 sm:p-6 w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4 sm:gap-0">
          <h6 className={`font-medium ${formData.type === "buy" ? "text-text-success" : "text-text-error"}`}>
            {formData.type === "buy" ? "Buy" : "Sell"} <span className="text-text-primary">Token</span>
          </h6>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1 w-full sm:w-auto">
              <TokenChip tokenAddress={formData.tokenAddress} networkId={formData.traderChain} />
              <FaArrowRight className="text-icons-icon-tertiary shrink-0" />
              <NetworkChip networkId={formData.traderChain} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Fiat Amount */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Fiat Amount</p>
            <p className="text-text-primary">
              {fiatAmount.toLocaleString()} {formData.currency}
            </p>
          </div>

          {/* Price */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Price Limit</p>
            <p className="text-text-primary">
              {formData.priceLimit.toLocaleString()} {formData.currency}/Token
            </p>
          </div>

          {/* Total Quantity */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Total Quantity</p>
            <p className="text-text-primary">{formData.quantity.toLocaleString()} Tokens</p>
          </div>

          {/* Payment Method */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Payment Method</p>
            <p className="text-text-primary">{formData.paymentMethod}</p>
          </div>

          {/* Token Address */}
          <div className="flex items-center justify-between">
            <p className="text-text-tertiary">Token Address</p>
            <p className="text-text-primary text-sm">
              {formData.tokenAddress.slice(0, 6)}...{formData.tokenAddress.slice(-4)}
            </p>
          </div>
        </div>
      </div>

      {/* Cancel Button */}
      <Button onClick={onCancel} variant="outline" size="lg" width="full" className="mt-4">
        Cancel Search
      </Button>
    </div>
  );
}
